// Material 3 Icon Button Component
// Aligned with Google Material Web (material-components/material-web/docs/components/icon-button.md)
// Supports: Standard, Filled, Filled Tonal, Outlined types with toggle state support & forwardRef
(function () {
  const { IconButton: MuiIconButton } = MaterialUI;

  const IconButton = React.forwardRef(function IconButton({
    variant = 'standard', // 'standard' | 'filled' | 'tonal' | 'outlined'
    size = 'medium',       // 'small' | 'medium' | 'large'
    selected = false,      // toggle support
    color = 'default',
    children,
    disabled = false,
    href,
    target,
    sx = {},
    ...props
  }, ref) {
    const theme = MaterialUI.useTheme();
    const isDark = theme?.palette?.mode !== 'light';

    let dim = 40;
    if (size === 'small') dim = 32;
    if (size === 'large') dim = 48;

    let baseStyles = {
      minWidth: dim,
      minHeight: dim,
      width: sx.width || (sx.p ? 'auto' : dim),
      height: sx.height || (sx.p ? 'auto' : dim),
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)'
    };

    const colors = theme?.palette?.m3 || (isDark ? window.Components?.m3DarkColors : window.Components?.m3LightColors) || {};

    switch (variant) {
      case 'filled':
        baseStyles.backgroundColor = selected
          ? (colors.primaryContainer || (isDark ? '#4f378b' : 'var(--m3-on-primary-container)'))
          : (colors.primary || 'var(--m3-primary)');
        baseStyles.color = selected
          ? (colors.onPrimaryContainer || (isDark ? 'var(--m3-on-primary-container)' : '#21005d'))
          : (colors.onPrimary || (isDark ? '#381e72' : '#ffffff'));
        baseStyles['&:hover'] = {
          backgroundColor: selected
            ? (colors.primaryContainer || '#4f378b')
            : (colors.primary || 'var(--m3-primary)'),
          filter: 'brightness(1.08)',
          transform: 'scale(1.05)'
        };
        break;

      case 'tonal':
      case 'filled-tonal':
        baseStyles.backgroundColor = selected
          ? (colors.secondaryContainer || 'color-mix(in srgb, var(--m3-primary) 24%, transparent)')
          : (colors.surfaceContainerHighest || (isDark ? '#36343b' : '#e8def8'));
        baseStyles.color = selected
          ? (colors.onSecondaryContainer || 'var(--m3-primary)')
          : (colors.onSurfaceVariant || (isDark ? 'var(--m3-on-surface)' : '#1d192b'));
        baseStyles['&:hover'] = {
          backgroundColor: selected
            ? (colors.secondaryContainer || '#4a4458')
            : (colors.surfaceContainerHighest || '#36343b'),
          filter: 'brightness(1.08)'
        };
        break;

      case 'outlined':
        baseStyles.backgroundColor = 'transparent';
        baseStyles.border = selected
          ? `1px solid ${colors.primary || 'var(--m3-primary)'}`
          : `1px solid ${colors.outlineVariant || ('var(--m3-outline-variant)')}`;
        baseStyles.color = selected
          ? (colors.primary || 'var(--m3-primary)')
          : (colors.onSurfaceVariant || (isDark ? '#cac4d0' : '#49454f'));
        baseStyles['&:hover'] = {
          borderColor: colors.primary || 'var(--m3-primary)',
          backgroundColor:'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)'};
        break;

      case 'standard':
      default:
        baseStyles.backgroundColor = selected
          ? 'color-mix(in srgb, var(--m3-on-surface) 12%, transparent)'
          : 'transparent';
        baseStyles.color = selected
          ? (colors.primary || 'var(--m3-primary)')
          : 'inherit';
        baseStyles['&:hover'] = {
          backgroundColor:'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)'};
        break;
    }

    return (
      <MuiIconButton
        ref={ref}
        disabled={disabled}
        href={href}
        target={target}
        sx={{
          ...baseStyles,
          // Touch screens: the tap area grows to at least 44dp around the drawn button (nothing changes visually)
          '@media (pointer: coarse)': { '&::after': { content: '""', position: 'absolute', inset: 'min(0px, calc((100% - 44px) / 2))' } },
          // A caller-supplied background stays on hover / after a tap (touch keeps :hover), with the state layer on top of it
          ...(sx.backgroundColor && !sx['&:hover'] && { '&:hover': { backgroundColor: `color-mix(in srgb, currentColor 8%, ${sx.backgroundColor})` } }),
          ...sx
        }}
        {...props}
      >
        {children}
      </MuiIconButton>
    );
  });

  const FilledIconButton = React.forwardRef((props, ref) => <IconButton ref={ref} variant="filled" {...props} />);
  const FilledTonalIconButton = React.forwardRef((props, ref) => <IconButton ref={ref} variant="tonal" {...props} />);
  const OutlinedIconButton = React.forwardRef((props, ref) => <IconButton ref={ref} variant="outlined" {...props} />);

  window.Components = window.Components || {};
  window.Components.IconButton = IconButton;
  window.Components.FilledIconButton = FilledIconButton;
  window.Components.FilledTonalIconButton = FilledTonalIconButton;
  window.Components.OutlinedIconButton = OutlinedIconButton;
})();
